import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MinigameViewProps } from '../../types/minigame';

// Log Scrubber (level 25) — the memory rung. Your session marker is announced up front, then the
// target's log replays ONE ENTRY AT A TIME; afterwards you pick your own entries out of a line-up
// salted with near-misses.
//
// There is deliberately NO scrollback: holding the entries is the game, so the component shows
// only the entry currently passing. The server paces the stream from its own clock and hands over
// nothing before its moment — this view just polls and displays what it is given.
//
// Two things the Deep Core taught us, applied here on purpose:
//   * the sweep starts on an explicit Begin, so it can't scroll past somebody still reading
//     what their marker is;
//   * the audit has ONE confirm button, always visible with its count on it — a hidden second
//     step under a clock reads as "it ignored my answer".

interface Entry {
  index: number;
  time: string;
  src: string;
  action: string;
  marker: string;
  preMarked: boolean;
  /** How long this entry holds the screen — longer for your own. */
  holdMs: number;
}

interface Candidate {
  id: number;
  time: string;
  src: string;
  action: string;
  marker: string;
}

interface Challenge {
  marker: string;
  totalEntries: number;
  intervalMs: number;
  /** The whole sweep's length, summed over the entries' own holds. */
  sweepMs: number;
  started: boolean;
  streamedCount: number;
  sweepDone: boolean;
  preMarks: number;
  falsePositiveTolerance: number;
  /** Entries that have just come due. Never a scrollback of the whole log. */
  fresh: Entry[];
  /** Empty until the sweep is over. */
  candidates: Candidate[];
  timeLimitSec: number;
}

/** How often we ask the server whether another entry has come due. */
const POLL_MS = 250;

export default function LogScrubberView({
  challenge, onAct, onSubmit, setPendingSubmission, busy,
}: MinigameViewProps<Challenge, Challenge>) {
  const [live, setLive] = useState<Challenge | null>(challenge ?? null);
  const [current, setCurrent] = useState<Entry | null>(null);
  const [shown, setShown] = useState(0);
  const [selected, setSelected] = useState<number[]>([]);

  // Entries the server has handed over but that haven't had their moment on screen yet.
  const queueRef = useRef<Entry[]>([]);
  const pollingRef = useRef(false);
  const seenRef = useRef<Set<number>>(new Set());
  /** When the entry on screen went up, so the ticker paces itself without re-running.  */
  const lastShownAtRef = useRef(0);

  const started = live?.started ?? false;
  const sweepDone = live?.sweepDone ?? false;
  const total = live?.totalEntries ?? 0;
  const intervalMs = live?.intervalMs ?? 1200;
  const candidates = useMemo(() => live?.candidates ?? [], [live]);
  const tolerance = live?.falsePositiveTolerance ?? 1;

  useEffect(() => {
    setLive(challenge ?? null);
    setCurrent(null);
    setShown(0);
    setSelected([]);
    queueRef.current = [];
    seenRef.current = new Set();
    lastShownAtRef.current = 0;
    setPendingSubmission({ ids: [] });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [challenge]);

  const absorb = useCallback((next: Challenge) => {
    setLive(next);
    for (const e of next.fresh ?? []) {
      // The server can repeat nothing, but a retried poll must not double-queue an entry.
      if (seenRef.current.has(e.index)) continue;
      seenRef.current.add(e.index);
      queueRef.current.push(e);
    }
  }, []);

  // Poll while the sweep is running. One request in flight at a time: overlapping polls would
  // race each other into the queue.
  useEffect(() => {
    if (!started || sweepDone) return;
    const id = setInterval(() => {
      // The ref alone is the guard. Depending on `busy` here would tear the interval down and
      // rebuild it on every poll, since a poll is itself what sets it.
      if (pollingRef.current) return;
      pollingRef.current = true;
      void (async () => {
        try {
          const next = await onAct({ kind: 'poll' });
          if (next) absorb(next);
        } finally {
          pollingRef.current = false;
        }
      })();
    }, POLL_MS);
    return () => clearInterval(id);
  }, [started, sweepDone, onAct, absorb]);

  // Give each entry its moment. Deliberately a STABLE ticker keyed only on the sweep and its
  // pace: an earlier version was a setTimeout that also depended on `live`, which changes on
  // every poll — so the timeout was cancelled and rebuilt every 250ms and never got to fire.
  // The display froze on the first entry and then jumped to the audit, which read as the game
  // running far too fast to follow.
  useEffect(() => {
    if (!started) return;
    const id = setInterval(() => {
      if (queueRef.current.length === 0) return;
      const now = Date.now();
      // First entry appears at once; afterwards one per interval, quicker if a backlog built up
      // behind a slow poll so nothing is ever dropped.
      // Each entry keeps the screen for its OWN hold — yours are given longer, because they are
      // the ones that have to be carried into the audit.
      const hold = current?.holdMs && current.holdMs > 0 ? current.holdMs : Math.max(400, intervalMs);
      const wait = Math.max(400, hold) / Math.max(1, queueRef.current.length);
      if (lastShownAtRef.current !== 0 && now - lastShownAtRef.current < wait) return;

      const next = queueRef.current.shift();
      if (!next) return;
      lastShownAtRef.current = now;
      setCurrent(next);
      setShown(n => n + 1);
    }, 100);
    return () => clearInterval(id);
  }, [started, intervalMs, current]);

  const begin = async () => {
    if (busy || started) return;
    const next = await onAct({ kind: 'begin' });
    if (next) absorb(next);
  };

  const toggle = (id: number) => {
    if (busy) return;
    const next = selected.includes(id) ? selected.filter(x => x !== id) : [...selected, id];
    setSelected(next);
    setPendingSubmission({ ids: next });
  };

  const purge = () => {
    if (busy || selected.length === 0) return;
    // The audit is the FINAL answer, so it goes through submit — the same path the host uses if
    // the round clock runs out, which keeps both routes graded identically.
    onSubmit({ ids: selected });
  };

  const marker = live?.marker ?? '???';
  const mineHere = current !== null && current.marker === marker;

  const row: React.CSSProperties = { fontFamily: 'monospace', fontSize: '0.8rem' };

  return (
    <div style={{ fontFamily: 'monospace' }}>
      {/* Your marker — the one thing you need before anything moves, and useful again in the audit */}
      <div style={{
        display: 'flex', alignItems: 'center', gap: '0.75rem', flexWrap: 'wrap',
        border: '1px solid var(--border-color)', borderRadius: 4, padding: '0.5rem 0.7rem',
        marginBottom: '0.7rem', background: '#0d1117',
      }}>
        <span style={{ fontSize: '0.72rem', color: 'var(--text-muted)', textTransform: 'uppercase' }}>your session</span>
        <strong style={{ fontSize: '1.15rem', color: '#fbbf24', letterSpacing: '0.08em' }}>session={marker}</strong>
        {(live?.preMarks ?? 0) > 0 && (
          <span style={{ fontSize: '0.72rem', color: '#60a5fa' }}>
            your Hider flags {live!.preMarks} of them for you
          </span>
        )}
      </div>

      {!started ? (
        <div style={{
          border: '1px solid #232833', borderRadius: 4, padding: '0.9rem', background: '#0d1117',
        }}>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.5rem', lineHeight: 1.5 }}>
            The target's log is about to replay, <strong>one entry at a time</strong>. Every entry
            carrying <strong style={{ color: '#fbbf24' }}>session={marker}</strong> is yours and has
            to go. You are <strong>not</strong> told how many there are, and there is no scrollback —
            watch it all the way through.
          </p>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.5rem', lineHeight: 1.5 }}>
            Afterwards you have to pick them out of a line-up that includes <strong>fakes carrying
            your marker</strong> with one field changed — so note <em>what</em> each of your entries
            said, not just that it was yours.
          </p>
          <p style={{ ...row, color: 'var(--text-muted)', margin: '0 0 0.7rem' }}>
            {total} entries, about {((live?.sweepMs ?? total * intervalMs) / 1000).toFixed(0)} seconds —
            and the ones that are yours stay on screen longer, so you have time to take them in. The
            round clock is already running, so don't dawdle — but nothing streams until you say so.
          </p>
          <button className="primary" onClick={begin} disabled={busy} style={{ fontSize: '0.85rem', padding: '0.4rem 1rem' }}>
            Begin sweep
          </button>
        </div>
      ) : !sweepDone ? (
        <>
          <div style={{
            border: `1px solid ${mineHere ? '#fbbf24' : '#232833'}`,
            borderRadius: 4, padding: '1rem', marginBottom: '0.6rem', textAlign: 'center',
            background: mineHere ? '#1a1608' : '#0d1117',
          }}>
            {current === null ? (
              <div style={{ color: '#4b5563', fontSize: '0.9rem' }}>reading the log…</div>
            ) : (
              <>
                <div style={{ ...row, fontSize: '0.95rem', color: mineHere ? '#fde047' : '#cbd5e1', letterSpacing: '0.04em' }}>
                  [{current.time}] {current.src} {current.action}{' '}
                  <strong style={{ color: mineHere ? '#fbbf24' : '#6b7280' }}>session={current.marker}</strong>
                </div>
                <div style={{ fontSize: '0.72rem', marginTop: '0.35rem', color: current.preMarked ? '#60a5fa' : mineHere ? '#fbbf24' : '#4b5563' }}>
                  {current.preMarked ? 'flagged by your Hider — yours' : mineHere ? 'this one is yours' : 'not yours'}
                </div>
              </>
            )}
          </div>
          <div style={{ height: 4, background: '#1a1a2e', borderRadius: 2, overflow: 'hidden' }}>
            <div style={{
              width: `${total > 0 ? Math.min(100, (shown / total) * 100) : 0}%`, height: '100%',
              background: 'var(--primary-color)', transition: 'width 0.2s',
            }} />
          </div>
          <div style={{ ...row, color: 'var(--text-muted)', fontSize: '0.74rem', marginTop: '0.3rem' }}>
            entry {Math.min(shown, total)} of {total} — remember the ones that are yours
          </div>
        </>
      ) : (
        <>
          <p style={{ ...row, color: '#cbd5e1', margin: '0 0 0.5rem', lineHeight: 1.5 }}>
            <strong>The audit.</strong> Purge an entry when <strong>both</strong> are true:
          </p>
          <ul style={{ ...row, color: '#cbd5e1', margin: '0 0 0.5rem', paddingLeft: '1.2rem', lineHeight: 1.6 }}>
            <li>it carries <strong style={{ color: '#fbbf24' }}>session={marker}</strong>, and</li>
            <li>you <strong>actually saw it</strong> go past in the log.</li>
          </ul>
          <p style={{ ...row, color: 'var(--text-muted)', margin: '0 0 0.7rem', fontSize: '0.76rem', lineHeight: 1.5 }}>
            The marker on its own is not enough: some of these carry it but were never in the log —
            one field has been altered. Purging one of those is what trips the audit. Leave one of
            yours behind and the trace completes; purging an innocent entry is survivable{' '}
            <strong>{tolerance === 1 ? 'once' : `${tolerance} times`}</strong> at a cost in reward,
            and fatal beyond that.
          </p>

          <div style={{ display: 'grid', gap: '0.3rem', marginBottom: '0.8rem' }}>
            {candidates.map(c => {
              const on = selected.includes(c.id);
              const sameMarker = c.marker === marker;
              return (
                <button
                  key={c.id}
                  onClick={() => toggle(c.id)}
                  disabled={busy}
                  style={{
                    ...row, textAlign: 'left', padding: '0.4rem 0.6rem',
                    borderColor: on ? '#f87171' : undefined,
                    color: on ? '#fca5a5' : '#cbd5e1',
                    background: on ? 'rgba(248,113,113,0.08)' : undefined,
                  }}
                >
                  <span style={{ color: on ? '#f87171' : '#4b5563', marginRight: '0.5rem' }}>
                    {on ? 'PURGE' : '·····'}
                  </span>
                  [{c.time}] {c.src} {c.action}{' '}
                  <strong style={{ color: sameMarker ? '#fbbf24' : '#6b7280' }}>session={c.marker}</strong>
                </button>
              );
            })}
          </div>

          <div style={{ display: 'flex', gap: '0.5rem', alignItems: 'center', flexWrap: 'wrap' }}>
            <button
              className="primary"
              onClick={purge}
              disabled={busy || selected.length === 0}
              style={{ fontSize: '0.85rem', padding: '0.35rem 0.9rem' }}
            >
              {busy ? '…' : `Purge ${selected.length} selected`}
            </button>
            <button
              onClick={() => { setSelected([]); setPendingSubmission({ ids: [] }); }}
              disabled={busy || selected.length === 0}
              style={{ fontSize: '0.78rem', padding: '0.3rem 0.7rem' }}
            >
              clear
            </button>
            <span style={{ ...row, color: 'var(--text-muted)', fontSize: '0.74rem' }}>
              tap an entry to mark it for deletion
            </span>
          </div>
        </>
      )}
    </div>
  );
}
